import React, { useState, useEffect } from 'react';
import { 
    Box, List, ListItemButton, ListItemAvatar, ListItemText, 
    Avatar, Typography, Divider, CircularProgress, TextField, 
    InputAdornment, Paper, useTheme, useMediaQuery 
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import ChatWindow from './ChatWindow'; 
import { getAiConversations } from '../../../../controllers/company/ai/AiChatController';

export default function AiOperationsCenter() {
    const [chatList, setChatList] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [isListLoading, setIsListLoading] = useState(false);
    const [listSearch, setListSearch] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // 🔥 CORRECCIÓN 1: Blindaje del temporizador para evitar "Stale Closures"
    useEffect(() => {
        const fetchChatList = async (isBackground = false) => {
            if (!isBackground) setIsListLoading(true);
            try {
                const response = await getAiConversations();
                if (response && response.data) {
                    const newChats = response.data.chats || response.data;
                    setChatList(newChats);
                    
                    // Actualizamos el chat seleccionado buscando siempre en el estado más reciente (prev)
                    setSelectedChat(prevSelected => {
                        if (prevSelected) {
                            const updated = newChats.find(c => c.id === prevSelected.id);
                            return updated || prevSelected;
                        }
                        return prevSelected;
                    });
                }
            } catch (error) {
                console.error("Error cargando la lista de chats:", error);
            } finally {
                if (!isBackground) setIsListLoading(false);
            }
        };

        fetchChatList(false); 
        const listIntervalId = setInterval(() => fetchChatList(true), 10000);
        return () => clearInterval(listIntervalId);
    }, []);

    // 🔥 Filtrado y Ordenamiento perfecto usando la fecha completa (updated_at)
    const filteredAndSortedChats = chatList
        .filter(chat => 
            (chat.display_name || chat.wa_phone_number || '').toLowerCase().includes(listSearch.toLowerCase())
        )
        .sort((a, b) => {
            // Evaluamos la nueva propiedad 'updated_at' que nos manda Laravel
            const dateA = new Date(a.updated_at ? a.updated_at.replace(' ', 'T') : 0).getTime();
            const dateB = new Date(b.updated_at ? b.updated_at.replace(' ', 'T') : 0).getTime();
            
            return dateB - dateA; // El más reciente (número mayor) sube al primer lugar
        });

    // 🔥 Formateador de Fecha y Hora (Ej: "14 ago, 08:32 PM")
    const formatListDate = (dateString) => {
        if (!dateString) return '';
        
        // Aseguramos compatibilidad cruzada de navegadores cambiando el espacio por 'T'
        const date = new Date(dateString.replace(' ', 'T'));
        
        // Si por alguna razón la fecha es inválida, devolvemos un texto vacío
        if (isNaN(date.getTime())) return '';

        return date.toLocaleString('es-VE', { 
            day: '2-digit', 
            month: 'short', 
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: true 
        });
    };

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                border: '1px solid #e0e0e0', 
                borderRadius: 4, 
                height: { xs: 'calc(100vh - 120px)', md: '700px' }, 
                display: 'flex', 
                overflow: 'hidden', 
                mt: 2 
            }}
        >
            {/* PANEL IZQUIERDO: LISTA DE CHATS */}
            {(!isMobile || !selectedChat) && (
                <Box 
                    sx={{ 
                        width: isMobile ? '100%' : '350px', 
                        flexShrink: 0, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
                        bgcolor: '#fff' 
                    }}
                >
                    <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
                        <TextField
                            fullWidth size="small" placeholder="Buscar cliente..."
                            value={listSearch} onChange={(e) => setListSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: '#9ca3af' }} />
                                    </InputAdornment>
                                ),
                                sx: { 
                                    borderRadius: 3, bgcolor: '#f9fafb',
                                    '& fieldset': { borderColor: 'transparent' },
                                    '&:hover fieldset': { borderColor: '#e5e7eb' },
                                    '&.Mui-focused fieldset': { borderColor: 'primary.main' } 
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                        {isListLoading ? (
                            <Box display="flex" justifyContent="center" mt={4}><CircularProgress size={24} /></Box>
                        ) : filteredAndSortedChats.length === 0 ? (
                            <Box p={3} textAlign="center">
                                <Typography variant="body2" color="text.secondary">No hay conversaciones activas.</Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {filteredAndSortedChats.map((chat) => {
                                    const isSelected = selectedChat?.id === chat.id;
                                    return (
                                        <React.Fragment key={chat.id}>
                                            <ListItemButton 
                                                selected={isSelected}
                                                onClick={() => setSelectedChat(chat)}
                                                sx={{
                                                    p: 2,
                                                    bgcolor: isSelected ? '#f0f4f8' : 'transparent',
                                                    '&:hover': { bgcolor: '#f8f9fa' }
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: isSelected ? 'primary.main' : '#f0f0f0', color: isSelected ? '#fff' : '#888' }}>
                                                        <PersonIcon fontSize="small" />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                                            <Typography variant="body2" fontWeight="700" noWrap sx={{ color: '#111' }}>
                                                                {chat.display_name || chat.phone}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                                                                {/* Mandamos la fecha completa al formateador */}
                                                                {formatListDate(chat.updated_at)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ mt: 0.5, display: 'block' }}>
                                                            {chat.last_message || 'Sin mensajes aún...'}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                            <Divider component="li" sx={{ borderColor: '#f5f5f5' }} />
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        )}
                    </Box>
                </Box>
            )}

            {/* PANEL DERECHO: VENTANA DE CHAT */}
            {(!isMobile || selectedChat) && (
                <Box 
                    sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        width: isMobile ? '100%' : 'calc(100% - 350px)',
                        bgcolor: '#f8f9fa' 
                    }}
                >
                    {selectedChat ? (
                        <ChatWindow 
                            chat={selectedChat} 
                            isMobile={isMobile} 
                            onBack={() => setSelectedChat(null)} 
                        />
                    ) : (
                        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="500">
                                Selecciona una conversación para comenzar
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Paper>
    );
}